import {
    Box,
    Button,
    createStyles,
    IconButton,
    makeStyles,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
} from '@material-ui/core';
import {v4} from 'uuid';
import React, { useState } from 'react';
import {useTranslation} from 'react-i18next';
import i18nNamespaces from '../../../const/i18nNamespaces';
import {useAppDispatch, useAppSelector} from '../../../hooks/customReduxHooks';
import {
    fetchPrescriptionHistory,
    fromDateSpecified,
    pageSpecified,
    selectCount,
    selectFromDateTime,
    selectPage,
    selectPrescriptionHistory,
    selectSize,
    sizeSpecified,
} from '../../../store/prescriptionHistorySlice';
import {AppointmentDetailsForPrescriptionDto} from '../../../types/appointments';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { DateTime } from 'luxon';
import { Refresh } from '@material-ui/icons';

type PrescriptionHistoryTableProps = {
    appointmentDetails: AppointmentDetailsForPrescriptionDto;
};

const useStyles = makeStyles(() =>
    createStyles({
        table: {
            minWidth: 400,
            '& .MuiTableHead-root .MuiTableCell-root': {
                fontSize: 16,
            },
        },
    }),
);

const PrescriptionHistoryTable = ({
    appointmentDetails,
}: PrescriptionHistoryTableProps) => {
    const classes = useStyles();
    const {t} = useTranslation(i18nNamespaces.PRESCRIPTIONS);
    const dispatch = useAppDispatch();
    const page = useAppSelector(selectPage);
    const size = useAppSelector(selectSize);
    const count = useAppSelector(selectCount);
    const prescriptionHistory = useAppSelector(selectPrescriptionHistory);
    const fromDateTime = useAppSelector(selectFromDateTime);
    const maxFromDate = DateTime.now().minus({ years: 1 }).toJSDate();
    const [datePickerOpen, setDatePickerOpen] = useState(false);

    const handleChangePage = (_: unknown, newPage: number) => {
        dispatch(pageSpecified(newPage));
        dispatch(fetchPrescriptionHistory(appointmentDetails));
    };

    const handleChangeRowsPerPage = async (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        dispatch(sizeSpecified(parseInt(event.target.value, 10)));
        dispatch(fetchPrescriptionHistory(appointmentDetails));
    };

    const handleFromDateChange = (date: Date | null) => {
        dispatch(fromDateSpecified(date));
        dispatch(pageSpecified(0));
        dispatch(fetchPrescriptionHistory(appointmentDetails));
        setDatePickerOpen(false);
    }

    return (
        <>
            <Box style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 30}}>
                 <KeyboardDatePicker
                    disableToolbar
                    variant="inline"
                    format="dd.MM.yyyy"
                    margin="none"
                    id="date-picker"
                    label={t('issueDateFrom')}
                    value={fromDateTime || null}
                    onChange={handleFromDateChange}
                    KeyboardButtonProps={{
                        'aria-label': 'change date',
                        onClick: () => setDatePickerOpen(true),
                    }}
                    onClick={() => setDatePickerOpen(true)}
                    maxDate={maxFromDate}
                    open={datePickerOpen}
                    onClose={() => setDatePickerOpen(false)}
                />
                <Button
                    variant={'outlined'}
                    color={'secondary'}
                    disableElevation
                    onClick={() =>
                        dispatch(fetchPrescriptionHistory(appointmentDetails))
                    }
                >
                    <Refresh color={'secondary'} />
                </Button>
            </Box>
            <TableContainer>
                <Table className={classes.table} size={'small'}>
                    <TableHead>
                        <TableRow>
                            <TableCell padding='none'>{t('issueDate')}</TableCell>
                            <TableCell>{t('status')}</TableCell>
                            <TableCell align="left">{t('drugName')}</TableCell>
                            <TableCell align="right">
                                {t('drugCount')}
                            </TableCell>
                            <TableCell align="right">{t('packSize')}</TableCell>
                            <TableCell align="right" padding='none'>
                                {t('paymentRate')}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {prescriptionHistory?.map(prescription => (
                            <TableRow key={prescription.prescriptionKey + v4()}>
                                <TableCell component="th" scope="row" padding='none'>
                                    {new Date(
                                        prescription.createDateTime,
                                    ).toLocaleDateString()}
                                </TableCell>
                                <TableCell align="left">
                                    {prescription.prescriptionStatus}
                                </TableCell>
                                <TableCell align="left">
                                    {prescription.medicinalProductName}
                                </TableCell>
                                <TableCell align="right">
                                    {prescription.quantity}
                                </TableCell>
                                <TableCell align="right">
                                    {prescription.packageSize}
                                </TableCell>
                                <TableCell align="right">
                                    {prescription.paymentRate}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={count}
                rowsPerPage={size}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                SelectProps={{style: {width: 'auto'}}}
            />
        </>
    );
};

export default PrescriptionHistoryTable;
